import { useState } from 'react';
import {
  Check,
  ChevronDown,
  ExternalLink,
  FileDown,
  FileSpreadsheet,
  FileText,
  Loader2,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../components/ui/utils';
import { readRemembered, writeRemembered } from '../../../shared/lib/remembered';

export type ExportFormat = {
  id: string;
  label: string;
  /** Строка под пунктом меню: чем этот вариант отличается от соседних. */
  description?: string;
  icon?: LucideIcon;
  disabled?: boolean;
};

/** Частые форматы отчётов — чтобы подписи были одинаковыми во всех карточках. */
export const FORMAT_XLSX: ExportFormat = { id: 'xlsx', label: 'Excel', description: 'Скачать файл .xlsx', icon: FileSpreadsheet };
export const FORMAT_DOCX: ExportFormat = { id: 'docx', label: 'Word', description: 'Скачать файл .docx', icon: FileText };
export const FORMAT_GDOC: ExportFormat = {
  id: 'gdoc',
  label: 'Google Docs',
  description: 'Создать документ в вашем Google Диске и открыть',
  icon: ExternalLink,
};

const GLOBAL_KEY = 'export:last';

/**
 * Какой формат предложить первым: последний выбранный в этой карточке, иначе
 * последний выбранный вообще (человек обычно сдаёт всё в одном виде), иначе
 * первый из списка.
 */
export function pickExportFormat(
  ids: readonly string[],
  own: string | null,
  global: string | null,
): string | undefined {
  if (own && ids.includes(own)) return own;
  if (global && ids.includes(global)) return global;
  return ids[0];
}

type ExportMenuProps = {
  formats: ExportFormat[];
  onSelect: (id: string) => void | Promise<void>;
  /**
   * С ключом — основная кнопка «<глагол>: <формат>» с запомненным форматом
   * и стрелка ▾ для другого. Без ключа — одно меню «<глагол> ▾».
   */
  rememberKey?: string;
  /** Глагол на кнопке: «Сформировать», «Выгрузить», «Скачать». */
  label?: string;
  busy?: boolean;
  busyLabel?: string;
  disabled?: boolean;
  variant?: 'default' | 'outline';
  size?: 'default' | 'sm';
  className?: string;
};

/**
 * Единая кнопка выгрузки отчётов «Статистики». Раньше у каждой карточки был
 * свой набор кнопок в своём порядке; теперь — одна кнопка с памятью формата.
 */
export function ExportMenu({
  formats,
  onSelect,
  rememberKey,
  label = 'Выгрузить',
  busy = false,
  busyLabel,
  disabled = false,
  variant = 'default',
  size = 'default',
  className,
}: ExportMenuProps): JSX.Element {
  const ids = formats.map((f) => f.id);
  const [chosen, setChosen] = useState<string | undefined>(() =>
    rememberKey
      ? pickExportFormat(ids, readRemembered<string | null>(`export:${rememberKey}`, null), readRemembered<string | null>(GLOBAL_KEY, null))
      : undefined,
  );
  const current = formats.find((f) => f.id === chosen) ?? (rememberKey ? formats[0] : undefined);

  function run(id: string): void {
    if (rememberKey) {
      setChosen(id);
      writeRemembered(`export:${rememberKey}`, id);
      writeRemembered(GLOBAL_KEY, id);
    }
    void onSelect(id);
  }

  const Icon = busy ? Loader2 : (current?.icon ?? FileDown);
  const menu = (
    <DropdownMenuContent align="end" className="w-72">
      {formats.map((f) => {
        const ItemIcon = f.icon ?? FileDown;
        return (
          <DropdownMenuItem key={f.id} disabled={f.disabled} onSelect={() => run(f.id)} className="items-start">
            <ItemIcon className="mt-0.5 size-4" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="font-medium">{f.label}</span>
              {f.description && <span className="text-xs text-muted-foreground">{f.description}</span>}
            </span>
            {rememberKey && f.id === current?.id && <Check className="mt-0.5 size-4" />}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );

  if (!rememberKey || !current) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} disabled={disabled || busy} className={cn('gap-2', className)}>
            <Icon className={cn('size-4', busy && 'animate-spin')} />
            {busy ? (busyLabel ?? 'Готовим…') : label}
            <ChevronDown className="size-3.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        {menu}
      </DropdownMenu>
    );
  }

  return (
    <div className={cn('inline-flex min-w-0', className)}>
      <Button
        variant={variant}
        size={size}
        disabled={disabled || busy || current.disabled}
        onClick={() => run(current.id)}
        className={cn('min-w-0 flex-1 gap-2', formats.length > 1 && 'rounded-r-none')}
      >
        <Icon className={cn('size-4', busy && 'animate-spin')} />
        <span className="truncate">{busy ? (busyLabel ?? 'Готовим…') : `${label}: ${current.label}`}</span>
      </Button>
      {formats.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={variant}
              size={size}
              disabled={disabled || busy}
              aria-label="Другой формат"
              title="Другой формат"
              className={cn('rounded-l-none border-l px-2', variant === 'default' && 'border-l-primary-foreground/30')}
            >
              <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          {menu}
        </DropdownMenu>
      )}
    </div>
  );
}
